/**
 * CSV download utility for the cn module (FE-4 / TM-1000001915).
 *
 * Produces a semicolon-separated CSV file that opens correctly in German Excel:
 *  - UTF-8 BOM (0xEF 0xBB 0xBF) so Excel detects the encoding.
 *  - Semicolon (;) as the column delimiter (German regional setting).
 *  - Each cell is double-quoted; embedded double-quotes are escaped as "".
 *  - Line endings: CRLF (\r\n) for maximum spreadsheet compatibility.
 *
 * Number formatting is the CALLER's responsibility. Pass pre-formatted strings
 * (e.g. "1.234,56" with German locale) in the rows array so the caller controls
 * precision, currency symbol, and locale.
 */

const UTF8_BOM = '﻿';
const DELIMITER = ';';
const LINE_ENDING = '\r\n';

/**
 * Escapes a single cell value for CSV output.
 * Wraps the value in double-quotes and escapes any embedded double-quotes.
 */
function escapeCell(value: string): string {
    return `"${value.replace(/"/g, '""')}"`;
}

/**
 * Converts an array of header strings and a 2-D array of row strings into a
 * UTF-8 BOM-prefixed, semicolon-delimited CSV string.
 *
 * @param headers - Column header labels (displayed in row 1).
 * @param rows    - Data rows; each inner array must have the same length as `headers`.
 * @returns The fully-formed CSV string ready to be used as Blob content.
 */
function buildCsv(headers: string[], rows: string[][]): string {
    const lines: string[] = [];
    lines.push(headers.map(escapeCell).join(DELIMITER));
    for (const row of rows) {
        lines.push(row.map(escapeCell).join(DELIMITER));
    }
    return UTF8_BOM + lines.join(LINE_ENDING);
}

/**
 * Builds a CSV from the given headers and rows and triggers a browser download.
 *
 * The download is performed by creating a temporary anchor element, assigning a
 * Blob URL, clicking it programmatically, and then revoking the URL. No server
 * round-trip is required.
 *
 * @param headers  - Column header labels.
 * @param rows     - Data rows (caller is responsible for German number formatting).
 * @param filename - Suggested file name for the download (e.g. "provisionssummen.csv").
 *                   The ".csv" extension is appended automatically if missing.
 *
 * @example
 * downloadCsv(
 *   ['Periode', 'AP (EUR)', 'BP (EUR)', 'Gesamt (EUR)'],
 *   [['2024-03', '1.234,56', '987,00', '2.221,56']],
 *   'provisionssummen-2024-03.csv'
 * );
 */
export function downloadCsv(headers: string[], rows: string[][], filename: string): void {
    const normalizedFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    const csvContent = buildCsv(headers, rows);
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.setAttribute('href', url);
    anchor.setAttribute('download', normalizedFilename);
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}
